import styled, { css } from 'styled-components'

import { MODE, SIZE } from './constants'

import { theme } from '../../../theme'

import type { StyledButtonProps } from './types'

const boldText = css`
  font-size: 16px;
  font-weight: 500;
`

export const Button = styled.button<StyledButtonProps>`
  display: flex;
  justify-content: center;
  align-items: center;

  column-gap: 10px;

  border: ${({ $border }) =>
    typeof $border === 'number'
      ? `${$border}px solid transparent`
      : ($border ?? 'none')};
  border-radius: 8px;

  width: ${({ $isFullWidth }) => ($isFullWidth ? '100%' : 'fit-content')};

  font-family: ${theme.fonts.mainFont};
  font-size: 14px;

  transition-duration: ${theme.animationSpeed.medium};

  &:disabled {
    cursor: default;
    background-color: ${theme.colors.violetWater};
  }

  ${({ $bold }) => $bold && boldText}

  /* mode */
  ${({ $styleMode, $isDanger, $border }) => {
    switch ($styleMode) {
      case MODE.primary:
        return css`
          background-color: ${$isDanger
            ? theme.colors.khmerCurry
            : theme.colors.sapphireDark};
          color: ${theme.colors.white};

          &:hover:not(:disabled),
          &:focus:not(:disabled) {
            background-color: ${$isDanger
              ? theme.colors.danger
              : theme.colors.blueExpanse};
          }
        `

      case MODE.secondary:
        return css`
          background-color: ${theme.colors.catskillWhite};
          color: ${theme.colors.sapphireDark};
          border: ${$border === 'none'
            ? 'none'
            : `${$border ?? 1}px solid ${theme.colors.lavenderSteel}`};

          &:hover:not(:disabled),
          &:focus:not(:disabled) {
            background-color: ${$isDanger
              ? theme.colors.danger
              : theme.colors.sapphireDark};
            color: ${theme.colors.white};
            svg {
              transition-duration: ${theme.animationSpeed.medium};
              color: ${theme.colors.white};
            }
          }
        `

      case MODE.secondaryLite:
        return css`
          background-color: ${theme.colors.white};
          color: ${theme.colors.sapphireDark};

          &:hover:not(:disabled),
          &:focus:not(:disabled) {
            background-color: ${$isDanger
              ? theme.colors.danger
              : theme.colors.sapphireDark};
            color: ${theme.colors.white};
            svg {
              color: ${theme.colors.white};
              transition-duration: ${theme.animationSpeed.medium};
            }
          }
        `

      case MODE.secondaryTransparent:
        return css`
          background-color: ${theme.colors.white};
          color: ${`${theme.colors.slateGray}80`};

          &:hover:not(:disabled),
          &:focus:not(:disabled) {
            background-color: ${$isDanger
              ? theme.colors.danger
              : theme.colors.white};
            color: ${theme.colors.sapphireDark};
            svg {
              color: ${theme.colors.sapphireDark};
              transition-duration: ${theme.animationSpeed.medium};
            }
          }
        `

      case MODE.borderedOnly:
        return css`
          background-color: inherit;
          border: ${$border === 'none'
            ? 'none'
            : `${$border ?? 1}px solid ${theme.colors.cautiousBlue}`};

          &:hover:not(:disabled),
          &:focus:not(:disabled) {
            background-color: ${$isDanger
              ? theme.colors.danger
              : theme.colors.white};
            color: ${theme.colors.sapphireDark};
            svg {
              color: ${theme.colors.sapphireDark};
              transition-duration: ${theme.animationSpeed.medium};
            }
          }
        `

      case MODE.secondaryBordered:
        return css`
          background-color: inherit;
          border: ${$border === 'none'
            ? 'none'
            : `${$border ?? 1}px solid ${theme.colors.cautiousBlue}`};

          &:hover:not(:disabled),
          &:focus:not(:disabled) {
            background-color: ${$isDanger
              ? theme.colors.danger
              : theme.colors.catskillWhite};
            color: ${theme.colors.sapphireDark};
            svg {
              color: ${theme.colors.sapphireDark};
              transition-duration: ${theme.animationSpeed.medium};
            }
          }
        `

      case MODE.link:
        return css`
          background-color: ${theme.colors.white};
          color: ${theme.colors.sapphireDark};
          text-decoration: underline;

          &:hover:not(:disabled),
          &:focus:not(:disabled) {
            cursor: pointer;
            color: ${theme.colors.blueSparkle};
          }
        `

      default:
        return css`
          background-color: ${theme.colors.sapphireDark};
          color: ${theme.colors.white};
        `
    }
  }}

  /* size */
${({ $styleSize }) => {
    switch ($styleSize) {
      case SIZE.big:
        return css`
          min-height: 44px;
          padding: 12px 27px;
          line-height: 20px;
          ${boldText}
        `

      case SIZE.small:
        return css`
          min-width: 90px;
          min-height: 36px;
          padding: 10px 24px;
        `

      case SIZE.medium:
        return css`
          min-width: 124px;
          font-size: 16px;
          height: 42px;
        `

      case SIZE.compact:
        return css`
          padding: 8px 13px;
          line-height: 0;
          min-width: 90px;
          height: 36px;
        `

      case SIZE.extrasmall:
        return css`
          font-size: 12px;
          padding: 5px 0;
          line-height: 16px;
          min-height: 0;
        `

      default:
        return css`
          min-height: 44px;
          padding: 12px 70px;
        `
    }
  }}
`
